import { Paragraph, Tab, Tabs } from '@movingimage-evp/mi-ui-component-library';
import { type JSX, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { DownloadReport } from './components/download-report/download-report';
import { EventSummaryChapters } from './components/event-summary-chapters/event-summary-chapters';
import { EventSummaryCuePoints } from './components/event-summary-cue-points/event-summary-cue-points';
import { EventSummaryHeader } from './components/event-summary-header/event-summary-header';
import { EventSummaryMetrics } from './components/event-summary-metrics/event-summary-metrics';
import { EventSummaryVideo } from './components/event-summary-video/event-summary-video';
import { ReportType, useGetWebcastSummaryManagerQuery } from '../../../../generated/graphql-manager';
import { useGetWebcastName } from '../../../hooks/use-get-webcast-name';

import styles from './event-summary.module.css';

export function EventSummary() {
  const { t } = useTranslation();
  const { webcastId = '' } = useParams();
  const name = useGetWebcastName() || '';
  const [slideMarkersNoteVisible, setSlideMarkersNoteVisible] = useState(true);

  const { data, refetch, startPolling, stopPolling } = useGetWebcastSummaryManagerQuery({
    variables: { webcastId },
    skip: !webcastId,
  });

  const webcast = data?.webcast;
  const session = data?.session;
  const avgPlaytime = webcast?.analytics?.avgPlaytime || 0;

  useEffect(() => {
    if (avgPlaytime < 0) startPolling(60000);
    return () => stopPolling();
  }, [avgPlaytime, startPolling, stopPolling]);

  if (!webcast) return null;

  const wasChatUsed = Boolean(webcast.chat?.settings.type);
  const vodEnabled = webcast.streaming?.vod?.enabled;
  const slidesLength = webcast.streaming?.vod?.slideMarkers?.length || 0;
  const chaptersLength = webcast.streaming?.vod?.chapters?.length || 0;

  const tabs = [
    <Tab key="video-file-tab" data-testid="video-file-tab" label={t('manager.eventSummary.recording.tabLabel')}>
      <EventSummaryVideo webcast={webcast} onVideoSelect={refetch} />
    </Tab>,
    slidesLength > 0 && (
      <Tab
        key="cue-points-tab"
        data-testid="cue-points-tab"
        label={t('manager.eventSummary.cuePoints.tabLabel', { count: slidesLength })}
      >
        <EventSummaryCuePoints webcast={webcast} />
      </Tab>
    ),
    vodEnabled && (
      <Tab
        key="chapters-tab"
        data-testid="chapters-tab"
        label={t('manager.eventSummary.chapters.tabLabel', { count: chaptersLength })}
      >
        <EventSummaryChapters
          webcast={webcast}
          slideMarkersNoteVisible={slideMarkersNoteVisible}
          onSlideMarkersNoteClose={() => setSlideMarkersNoteVisible(false)}
          onSave={refetch}
        />
      </Tab>
    ),
  ].filter(Boolean) as JSX.Element[];

  return (
    <section className={styles.container} data-testid="event-summary-page">
      <EventSummaryHeader
        webcast={webcast}
        webcastName={name}
        session={session}
        onEventArchiveProcess={refetch}
        onVideoSelect={refetch}
      />

      <EventSummaryMetrics webcast={webcast} session={session} />

      <section className={styles.onDemandSection}>
        <header>{t('manager.eventSummary.onDemandMode')}</header>
        <Tabs compact>{tabs}</Tabs>
      </section>

      <div className={styles.reportsWrapper}>
        <Paragraph>{t('manager.eventSummary.reports.title')}</Paragraph>

        <div className={styles.downloadButtonsWrapper}>
          <DownloadReport
            label={t('manager.eventSummary.reports.downloadViewers')}
            generateLabel={t('manager.eventSummary.reports.generateViewers')}
            infoText={t('manager.eventSummary.reports.infoIconTooltip.viewersReport')}
            webcastId={webcast.id}
            reportType={ReportType.VIEWS_REPORT}
          />

          <DownloadReport
            label={t('manager.eventSummary.reports.downloadRegisteredUsers')}
            generateLabel={t('manager.eventSummary.reports.generateRegisteredUsers')}
            infoText={t('manager.eventSummary.reports.infoIconTooltip.registeredUsersReport')}
            webcastId={webcast.id}
            reportType={ReportType.REGISTERED_USERS_REPORT}
          />

          {wasChatUsed && (
            <DownloadReport
              label={t('manager.eventSummary.reports.downloadChatMessages')}
              generateLabel={t('manager.eventSummary.reports.generateChatMessages')}
              infoText={t('manager.eventSummary.reports.infoIconTooltip.chatMessagesReport')}
              webcastId={webcast.id}
              reportType={ReportType.CHAT_MESSAGES_REPORT}
            />
          )}
        </div>
      </div>
    </section>
  );
}
